import "./PromoAccessories.scss";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { usePageLocalization } from "@sbi-international/boutik.gatsby.plugin.multilocale";
import { AllHeaterProductSingleModel } from "../../models/allHeaterProductSingleModel";
import { DroletButton } from "../primitives";
import GatsbyImageWrapper from "../GatsbyImageWrapper";
import { ServicesContext, useStore } from "@sbi-international/boutik.gatsby.plugin.services";

export default function PromoAccessories(props: {
    promoCMS: any;
    allProducts: Queries.allHeaterSingleNodeFragmentFragment[];
}) {
    const { currentLocale, navigateTranslated } = usePageLocalization();

    const servicesContext = useContext(ServicesContext);
    const productService = servicesContext?.proxy?.proxyService;
    const { currentStore } = useStore();

    const { t } = useTranslation("pages");
    const [reclaimableProductImage, setReclaimableProductImage] = useState<string>();

    const { promoCMS, allProducts } = props;
    const productWithPromo = allProducts?.find(
        (prod: any) =>
            prod.code === promoCMS?.attributes?.product_code?.values?.[0]?.[currentLocale]
    );
    const removeFirstPathname = (slug: string) => {
        return slug.split("/").slice(2).join("/") + "/";
    };

    const currentDate = new Date();

    const id = promoCMS?.id;
    const link = `/claim-promotion/?id=${id}`;

    useEffect(() => {
        if (promoCMS.attributes.claimable_product) {
            productService
                .getProductImage({
                    sku: promoCMS.attributes.claimable_product.values[0][currentLocale],
                    base64: true,
                    storeCode: currentStore ?? "all",
                })
                .then((image) => {
                    setReclaimableProductImage(image ?? "");
                });
        }
    }, []);

    const stringDateToDate = (dateString: string) => {
        const [day, month, year] = dateString.split("/");
        return new Date(Number(year), Number(month) - 1, Number(day));
    };

    const isPastDate = (endDateString: string, currentDate: Date) => {
        const endDate = stringDateToDate(endDateString);
        const currentDateOnly = new Date(
            currentDate.getFullYear(),
            currentDate.getMonth(),
            currentDate.getDate()
        );
        return endDate < currentDateOnly;
    };

    const formatDate = (from: string, to: string) => {
        const monthsInEnglish = [
            "January",
            "February",
            "March",
            "April",
            "May",
            "June",
            "July",
            "August",
            "September",
            "October",
            "November",
            "December",
        ];

        const monthsInFrench = [
            "Janvier",
            "Février",
            "Mars",
            "Avril",
            "Mai",
            "Juin",
            "Juillet",
            "Août",
            "Septembre",
            "Octobre",
            "Novembre",
            "Décembre",
        ];

        const fromDateSegment = from.split("/");
        const toDateSegment = to.split("/");

        const fromDay = fromDateSegment[0];
        const toDay = toDateSegment[0];

        const month =
            currentLocale == "fr_CA"
                ? monthsInFrench[Number(fromDateSegment[1]) - 1]
                : monthsInEnglish[Number(fromDateSegment[1]) - 1];
        const year = fromDateSegment[2];

        return t("promotions.from-to", {
            from: fromDay,
            to: toDay,
            month: month,
            year: year,
        });
    };

    return (
        <div className="promo-accessories-container">
            <div
                className="promo-image"
                onClick={() => {
                    navigateTranslated(link);
                }}
            >
                <GatsbyImageWrapper fields={productWithPromo?.fields} />
                <div
                    className={`claimable-product-container-mobile ${
                        reclaimableProductImage ? "visible" : ""
                    }`}
                >
                    <>
                        <img className="claimable-product-img" src={reclaimableProductImage} />
                        <p className="claimable-product-sku">
                            {promoCMS.attributes?.claimable_product?.values[0][currentLocale]}
                        </p>
                    </>
                </div>
            </div>

            <div className="content">
                <div className="top">
                    {isPastDate(
                        promoCMS.attributes?.end_date?.values[0][currentLocale],
                        currentDate
                    ) && (
                        <div className="expiration-banner">
                            <p>
                                {t("promotions.expiration-banner", {
                                    date: promoCMS?.attributes?.expiration_date?.values?.[0]?.[
                                        currentLocale
                                    ],
                                })}
                            </p>
                        </div>
                    )}
                    <span className="date">
                        {formatDate(
                            promoCMS?.attributes?.start_date?.values?.[0]?.[currentLocale],
                            promoCMS?.attributes?.end_date?.values?.[0]?.[currentLocale]
                        )}
                    </span>
                    <div className="content-line" />
                    <div className="content-title">{promoCMS?.title?.[currentLocale]}</div>
                    {promoCMS?.description && (
                        <div
                            className="content-description"
                            dangerouslySetInnerHTML={{
                                __html: promoCMS?.description?.valuesMarkdownRemark?.[
                                    currentLocale
                                ],
                            }}
                        />
                    )}
                    <div className="promo-options">
                        {promoCMS?.options?.map((option: any, index: number) => {
                            return (
                                <ListDiv
                                    key={index}
                                    item={option}
                                    currentLocale={currentLocale}
                                ></ListDiv>
                            );
                        })}
                    </div>
                </div>
                <div className="bottom">
                    <DroletButton
                        className="promo-button"
                        label={promoCMS?.attributes?.bouttons?.values?.[0]?.[currentLocale]}
                        onClick={() => {
                            navigateTranslated(link);
                        }}
                    />
                    {promoCMS.attributes?.promotion_condition.values.map((condition: any) => {
                        return <p className="promotion-conditions">{condition[currentLocale]}</p>;
                    })}
                </div>
            </div>
            <div
                className={`claimable-product-container-desktop ${
                    reclaimableProductImage ? "visible" : ""
                }`}
            >
                <img className="claimable-product-img" src={reclaimableProductImage} />
                {reclaimableProductImage && (
                    <p className="claimable-product-sku">
                        {promoCMS.attributes?.claimable_product?.values[0][currentLocale]}
                    </p>
                )}
            </div>
        </div>
    );
}

function ListDiv(props: { item: any; currentLocale: string }) {
    const { item, currentLocale } = props;
    return (
        <div className="list-div">
            <div className="list-sku">{item?.code}</div>
            <div className="list-title">{item?.title?.[currentLocale]}</div>
        </div>
    );
}
